import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Slider } from "../ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { AttackRowsEditor } from "./AttackRowsEditor";
import { Skull, TriangleAlert, DoorOpen, RefreshCw, Heart, Shield } from "lucide-react";

const HP_MIN = 20, HP_MAX = 1500, HP_STEP = 10;
const AC_MIN = 10, AC_MAX = 40, AC_STEP = 1;

export function BbebbegPanel({ raid, raidId, isMasterGm, api, formatApiErrorDetail }) {
  const [arenaTokens, setArenaTokens] = useState([]);
  const [sceneId, setSceneId] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [bossPick, setBossPick] = useState("");
  const [gateTemplatePick, setGateTemplatePick] = useState("");
  const [gateMax, setGateMax] = useState(10);
  const [busy, setBusy] = useState(false);
  const [hpDraft, setHpDraft] = useState(HP_MIN);
  const [acDraft, setAcDraft] = useState(AC_MIN);

  const loadArena = () => {
    if (!raid?.master_game_id) return;
    api.get(`/games/${raid.master_game_id}`).then(({ data }) => {
      setSceneId(data.current_scene_id || null);
      if (data.current_scene_id) {
        api.get(`/games/${raid.master_game_id}/tokens`, { params: { scene_id: data.current_scene_id } })
          .then((r) => setArenaTokens(r.data))
          .catch(() => setArenaTokens([]));
      }
    }).catch(() => {});
  };

  useEffect(() => {
    loadArena();
    api.get("/pf2e/conditions").then((r) => setConditions(r.data)).catch(() => setConditions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raid?.master_game_id, raid?.bbebbeg]);

  const bossToken = arenaTokens.find((t) => t.id === raid?.bbebbeg?.boss_token_id);

  useEffect(() => {
    if (!bossToken) return;
    setHpDraft(bossToken.hp_max ?? HP_MIN);
    setAcDraft(bossToken.stat_block?.ac ?? AC_MIN);
  }, [bossToken?.id, bossToken?.hp_max, bossToken?.stat_block?.ac]);

  if (!raid?.bbebbeg) return null;

  const patchBoss = async (updates) => {
    try {
      await api.patch(`/games/${raid.master_game_id}/tokens/${bossToken.id}`, updates);
      loadArena();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const commitHp = (v) => {
    const updates = { hp_max: v };
    // If the boss is currently at (or above, from a stale value) full health, bring current HP
    // along with the new max so it starts a fight fully healed — but if it's already mid-fight
    // and damaged, leave hp_current alone so this doesn't accidentally undo damage taken.
    if (bossToken.hp_current == null || bossToken.hp_current >= (bossToken.hp_max ?? 0)) updates.hp_current = v;
    patchBoss(updates);
  };

  const commitAc = (v) => patchBoss({ stat_block: { ...(bossToken.stat_block || {}), ac: v } });
  const commitSaveDc = (v) => patchBoss({ stat_block: { ...(bossToken.stat_block || {}), save_dc: v } });
  const commitAttacks = (attacks) => patchBoss({ stat_block: { ...(bossToken.stat_block || {}), attacks } });

  const designateBoss = async () => {
    if (!bossPick) return;
    setBusy(true);
    try {
      await api.post(`/raids/${raidId}/bbebbeg/boss-token`, { token_id: bossPick });
      toast.success("Boss designated");
      loadArena();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createFloodGate = async () => {
    if (!gateTemplatePick || !sceneId) return;
    const template = arenaTokens.find((t) => t.id === gateTemplatePick);
    setBusy(true);
    try {
      await api.post(`/games/${raid.master_game_id}/tokens`, {
        scene_id: sceneId, name: `Flood Gate: ${template?.name || "?"}`, token_type: "flood_gate",
        flood_gate_template_token_id: gateTemplatePick, flood_gate_max_spawns: Math.min(gateMax || 10, 10),
        x: 800, y: 500,
      });
      toast.success("Flood Gate created — drag it into position on the map");
      loadArena();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const triggerGate = async (tokenId) => {
    try {
      const { data } = await api.post(`/games/${raid.master_game_id}/tokens/${tokenId}/trigger-flood-gate`);
      toast.success(`${data.spawned.length} spawned!`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const npcCandidates = arenaTokens.filter((t) => t.token_type === "npc");
  const floodGates = arenaTokens.filter((t) => t.token_type === "flood_gate");
  const statBlock = bossToken?.stat_block || {};

  return (
    <div className="panel-wood rounded-2xl p-5 space-y-4 lg:col-span-2" data-testid="raid-bbebbeg-panel">
      <div>
        <h2 className="font-display text-2xl flex items-center gap-2"><Skull className="w-5 h-5 text-[#D90429]" /> BBEBBEG</h2>
        <p className="text-[10px] text-[#f1e0c5]/30">(Biggest, Baddest, Evilest, Big, Bad, Evil, Guy)</p>
      </div>

      <div className="flex items-start gap-2 bg-[#D90429]/10 border border-[#D90429]/30 rounded-xl px-3 py-2" data-testid="bbebbeg-disclaimer">
        <TriangleAlert className="w-4 h-4 text-[#D90429] flex-shrink-0 mt-0.5" />
        <p className="text-[11px] text-[#f1e0c5]/60">It's not perfect, please be creative — there's no real math for one creature vs. up to 100 players. This is a rough starting point, not a balance calculator.</p>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm uppercase tracking-widest text-[#f1e0c5]/40 flex items-center gap-2">
          <Skull className="w-3.5 h-3.5" /> The Boss
          <button data-testid="bbebbeg-refresh-arena-button" onClick={loadArena} className="ml-auto text-[#f1e0c5]/30 hover:text-[#3B82F6]"><RefreshCw className="w-3.5 h-3.5" /></button>
        </h3>
        {bossToken && <p className="text-sm">Currently: <strong className="text-[#D90429]">{bossToken.name}</strong></p>}
        {isMasterGm && (
          <div className="flex gap-2">
            <Select value={bossPick} onValueChange={setBossPick}>
              <SelectTrigger data-testid="bbebbeg-boss-token-select" className="bg-black/30 border-white/10 flex-1"><SelectValue placeholder="Pick a token on your arena map..." /></SelectTrigger>
              <SelectContent>
                {npcCandidates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="bbebbeg-designate-boss-button" disabled={!bossPick || busy} onClick={designateBoss} className="rounded-full bg-[#D90429] hover:bg-[#b8031f]">Designate</Button>
          </div>
        )}
        {!bossToken && <p className="text-[10px] text-[#f1e0c5]/30">Designate a boss token above to unlock its stat sheet — HP, AC, Save DC, and its own Attacks — below.</p>}
      </div>

      {bossToken && (
        <div className="border-t border-white/10 pt-4 space-y-3" data-testid="bbebbeg-boss-sheet">
          <h3 className="text-sm uppercase tracking-widest text-[#f1e0c5]/40">Boss Stat Sheet</h3>
          <div className="flex items-center gap-3">
            <span className="text-xs w-20 text-[#f1e0c5]/60 flex items-center gap-1"><Heart className="w-3 h-3 text-[#D90429]" /> HP</span>
            {isMasterGm ? (
              <Slider data-testid="bbebbeg-stat-slider-hp" value={[hpDraft]} min={HP_MIN} max={HP_MAX} step={HP_STEP} onValueChange={(v) => setHpDraft(v[0])} onValueCommit={(v) => commitHp(v[0])} className="flex-1" />
            ) : (
              <div className="flex-1 h-1.5 rounded-full bg-black/30 overflow-hidden"><div className="h-full bg-[#D90429]" style={{ width: `${Math.min(100, ((bossToken.hp_max ?? HP_MIN) / HP_MAX) * 100)}%` }} /></div>
            )}
            <span className="text-xs w-16 text-right" data-testid="bbebbeg-stat-value-hp">{bossToken.hp_current ?? bossToken.hp_max ?? hpDraft} / {isMasterGm ? hpDraft : (bossToken.hp_max ?? hpDraft)}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs w-20 text-[#f1e0c5]/60 flex items-center gap-1"><Shield className="w-3 h-3 text-[#3B82F6]" /> AC</span>
            {isMasterGm ? (
              <Slider data-testid="bbebbeg-stat-slider-ac" value={[acDraft]} min={AC_MIN} max={AC_MAX} step={AC_STEP} onValueChange={(v) => setAcDraft(v[0])} onValueCommit={(v) => commitAc(v[0])} className="flex-1" />
            ) : (
              <div className="flex-1 h-1.5 rounded-full bg-black/30 overflow-hidden"><div className="h-full bg-[#3B82F6]" style={{ width: `${Math.min(100, ((statBlock.ac ?? AC_MIN) / AC_MAX) * 100)}%` }} /></div>
            )}
            <span className="text-xs w-16 text-right" data-testid="bbebbeg-stat-value-ac">{isMasterGm ? acDraft : (statBlock.ac ?? AC_MIN)}</span>
          </div>
          {isMasterGm && (
            <div className="flex items-center gap-3">
              <span className="text-xs w-20 text-[#f1e0c5]/60">Save DC</span>
              <Input
                data-testid="bbebbeg-save-dc-input"
                type="number"
                defaultValue={statBlock.save_dc ?? 20}
                onBlur={(e) => commitSaveDc(parseInt(e.target.value, 10) || 0)}
                className="bg-black/30 border-white/10 h-8 text-xs w-24"
              />
            </div>
          )}
          {isMasterGm ? (
            <div className="space-y-1.5 pt-1">
              <p className="text-[10px] uppercase text-[#f1e0c5]/40">Attacks</p>
              <AttackRowsEditor attacks={statBlock.attacks || []} onChange={commitAttacks} conditions={conditions} testIdPrefix="bbebbeg" />
            </div>
          ) : (statBlock.attacks || []).length > 0 && (
            <div className="space-y-1 pt-1">
              <p className="text-[10px] uppercase text-[#f1e0c5]/40">Attacks</p>
              {statBlock.attacks.map((a, idx) => (
                <p key={idx} className="text-xs text-[#f1e0c5]/70">{a.name} <span className="text-[#D4AF37]">+{a.bonus}</span> {a.damage && <span className="text-[#f1e0c5]/40">— {a.damage}</span>}</p>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="border-t border-white/10 pt-4 space-y-3">
        <h3 className="text-sm uppercase tracking-widest text-[#f1e0c5]/40 flex items-center gap-2"><DoorOpen className="w-3.5 h-3.5" /> Flood Gates</h3>
        {isMasterGm && (
          <div className="flex flex-wrap gap-2 items-center" data-testid="bbebbeg-create-flood-gate-row">
            <Select value={gateTemplatePick} onValueChange={setGateTemplatePick}>
              <SelectTrigger data-testid="bbebbeg-gate-template-select" className="bg-black/30 border-white/10 flex-1 min-w-[160px]"><SelectValue placeholder="Which NPC spawns..." /></SelectTrigger>
              <SelectContent>
                {npcCandidates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="bbebbeg-gate-max-input" type="number" min={1} max={10} value={gateMax} onChange={(e) => setGateMax(parseInt(e.target.value) || 10)} className="bg-black/30 border-white/10 w-20" />
            <Button data-testid="bbebbeg-create-gate-button" disabled={!gateTemplatePick || busy} onClick={createFloodGate} className="rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab]">Create Gate</Button>
          </div>
        )}
        {floodGates.length === 0 ? (
          <p className="text-[10px] text-[#f1e0c5]/30">No Flood Gates yet — up to 10 GM-chosen reinforcements can pour out of one whenever it's triggered.</p>
        ) : (
          <div className="space-y-1.5">
            {floodGates.map((g) => (
              <div key={g.id} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2" data-testid={`bbebbeg-gate-${g.id}`}>
                <span className="text-sm">{g.name} <span className="text-[10px] text-[#f1e0c5]/30">(up to {g.flood_gate_max_spawns})</span></span>
                <Button data-testid={`bbebbeg-trigger-gate-${g.id}`} size="sm" variant="secondary" className="rounded-full" onClick={() => triggerGate(g.id)}>Trigger</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
